import { createBrowserRouter } from 'react-router';

import {
  FEATURE_FLAGS_CROP_PROTECTION,
  FEATURE_FLAGS_ENVIRONMENTAL_CROPS,
  FEATURE_FLAGS_SEED,
  REDIRECT_NOT_ALLOWED_URL,
  REDIRECT_TO_LOGIN_URL,
  ROUTE_CREATE_CROP_ORDER,
  ROUTE_CREATE_ENVIRONMENTAL_CROPS_ORDER,
  ROUTE_CREATE_ORDER,
  ROUTE_CREATE_SEED_ORDER,
  ROUTE_CROP_CUSTOMER_ORDERS,
  ROUTE_CROP_HOME,
  ROUTE_CROP_ORDERS_FAILED,
  ROUTE_CUSTOMER_ORDERS,
  ROUTE_ENVIRONMENTAL_CROPS_CUSTOMER_ORDERS,
  ROUTE_ENVIRONMENTAL_CROPS_HOME,
  ROUTE_ENVIRONMENTAL_CROPS_ORDERS_FAILED,
  ROUTE_ORDERS_FAILED,
  ROUTE_SEED_CUSTOMER_ORDERS,
  ROUTE_SEED_HOME,
  ROUTE_SEED_ORDERS_FAILED,
} from 'appConstants';
import Auth0ProviderWithNavigate from 'contexts/Auth0ProviderWithNavigate';
import RootErrorBoundary from 'pages/RootErrorBoundary';
import { AppLoader } from 'shared';

import AppStateProvider from './contexts/AppStateProvider';
import NotFound from './pages/NotFound';

const router = createBrowserRouter([
  {
    hydrateFallbackElement: <AppLoader />,
    errorElement: <RootErrorBoundary />,
    path: '/',
    element: <Auth0ProviderWithNavigate />,
    children: [
      {
        path: '/',
        element: <AppStateProvider />,
        children: [
          {
            path: '/',
            index: true,
            async lazy() {
              return {
                Component: (await import('./pages/Home')).default,
              };
            },
          },
          {
            path: ROUTE_ORDERS_FAILED,
            async lazy() {
              return {
                Component: (await import('./pages/FailedOrders')).default,
              };
            },
          },
          {
            path: `${ROUTE_CUSTOMER_ORDERS}/:customerId?`,
            async lazy() {
              return {
                Component: (await import('./pages/ViewOrder')).default,
              };
            },
          },
          {
            path: `${ROUTE_CREATE_ORDER}/:wizardStep?`,
            async lazy() {
              return {
                Component: (await import('./pages/CreateOrder')).default,
              };
            },
          },
          // Crop protection routes
          ...(FEATURE_FLAGS_CROP_PROTECTION
            ? [
                {
                  path: ROUTE_CROP_HOME,
                  children: [
                    {
                      index: true,
                      async lazy() {
                        return {
                          Component: (await import('./pages/CropProtection/Home')).default,
                        };
                      },
                    },
                    {
                      path: `${ROUTE_CREATE_CROP_ORDER}/:wizardStep?`,
                      async lazy() {
                        return {
                          Component: (await import('./pages/CropProtection/CreateOrder')).default,
                        };
                      },
                    },
                    {
                      path: `${ROUTE_CROP_CUSTOMER_ORDERS}/:customerId`,
                      async lazy() {
                        return {
                          Component: (await import('./pages/CropProtection/ViewOrder')).default,
                        };
                      },
                    },
                    {
                      path: ROUTE_CROP_ORDERS_FAILED,
                      async lazy() {
                        return {
                          Component: (await import('./pages/CropProtection/FailedOrders')).default,
                        };
                      },
                    },
                  ],
                },
              ]
            : []),
          // Seed routes
          ...(FEATURE_FLAGS_SEED
            ? [
                {
                  path: ROUTE_SEED_HOME,
                  children: [
                    {
                      index: true,
                      async lazy() {
                        return {
                          Component: (await import('./pages/Seed/Home')).default,
                        };
                      },
                    },
                    {
                      path: `${ROUTE_CREATE_SEED_ORDER}/:wizardStep?`,
                      async lazy() {
                        return {
                          Component: (await import('./pages/Seed/CreateOrder')).default,
                        };
                      },
                    },
                    {
                      path: `${ROUTE_SEED_CUSTOMER_ORDERS}/:customerId`,
                      async lazy() {
                        return {
                          Component: (await import('./pages/Seed/ViewOrder')).default,
                        };
                      },
                    },
                    {
                      path: ROUTE_SEED_ORDERS_FAILED,
                      async lazy() {
                        return {
                          Component: (await import('./pages/Seed/FailedOrders')).default,
                        };
                      },
                    },
                  ],
                },
              ]
            : []),
          // Environmental Crops routes
          ...(FEATURE_FLAGS_ENVIRONMENTAL_CROPS
            ? [
                {
                  path: ROUTE_ENVIRONMENTAL_CROPS_HOME,
                  children: [
                    {
                      index: true,
                      async lazy() {
                        return {
                          Component: (await import('./pages/EnvironmentalCrops/Home')).default,
                        };
                      },
                    },
                    {
                      path: `${ROUTE_CREATE_ENVIRONMENTAL_CROPS_ORDER}/:wizardStep?`,
                      async lazy() {
                        return {
                          Component: (await import('./pages/EnvironmentalCrops/CreateOrder'))
                            .default,
                        };
                      },
                    },
                    {
                      path: `${ROUTE_ENVIRONMENTAL_CROPS_CUSTOMER_ORDERS}/:customerId`,
                      async lazy() {
                        return {
                          Component: (await import('./pages/EnvironmentalCrops/ViewOrder')).default,
                        };
                      },
                    },
                    {
                      path: ROUTE_ENVIRONMENTAL_CROPS_ORDERS_FAILED,
                      async lazy() {
                        return {
                          Component: (await import('./pages/EnvironmentalCrops/FailedOrders'))
                            .default,
                        };
                      },
                    },
                  ],
                },
              ]
            : []),
          {
            path: '/callback',
            async lazy() {
              return {
                Component: (await import('./pages/LoginCallback')).default,
              };
            },
          },
        ],
      },
      {
        async lazy() {
          return {
            Component: (await import('./pages/LoginRedirect')).default,
          };
        },
        path: REDIRECT_TO_LOGIN_URL,
      },
      {
        async lazy() {
          return {
            Component: (await import('./pages/RedirectNotAllowed')).default,
          };
        },
        path: REDIRECT_NOT_ALLOWED_URL,
      },
      {
        element: <NotFound />,
        path: '*',
      },
    ],
  },
]);

export default router;
